import React, { useState } from "react";
import "../styles/ContactForm.css";
import emailjs from "@emailjs/browser";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    organisation: "",
    mobile: "",
    email: "",
    subject: "",
    message: "",
  });

  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      organisation: "",
      mobile: "",
      email: "",
      subject: "",
      message: "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      to_email: "connect@infinipoint.ai",
      subject: formData.subject,
      message: formData.message,
      organisation: formData.organisation,
      mobile_number: formData.mobile,
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE,
        process.env.REACT_APP_EMAILJS_TEMPLATE,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC
      )
      .then((response) => {
        console.log("SUCCESS!", response.status, response.text);
        setSubmitStatus({
          success: true,
          message: "Message sent successfully! We will get back to you soon.",
        });
        setShowPopup(true);
        resetForm();
        setTimeout(() => {
          setShowPopup(false);
        }, 3000);
      })
      .catch((error) => {
        console.log("FAILED...", error);
        setSubmitStatus({
          success: false,
          message: "Failed to send message. Please try again.",
        });
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
        }, 3000);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="form-container">
      {/* Bottom Center Popup */}
      {showPopup && (
        <div className="popup-container">
          <div
            className={`popup-message ${
              submitStatus.success ? "success" : "error"
            }`}
          >
            <div className="popup-content">
              {submitStatus.success ? (
                <svg
                  className="popup-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg
                  className="popup-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              )}
              <span>{submitStatus.message}</span>
            </div>
          </div>
        </div>
      )}

      <h1 className="form-title">Contact Us</h1>
      <p className="form-subtitle">
        Please fill out the form below and we'll get back to you as soon as
        possible.
      </p>

      <form onSubmit={handleSubmit} className="contact-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your name"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="organisation">Organisation</label>
            <input
              type="text"
              id="organisation"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              required
              placeholder="Enter your organisation"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Mobile Number</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
              placeholder="Enter your mobile number"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="form-full-width">
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Enter message subject"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Type your message here"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : "Submit Message"}
        </button>
      </form>

      <style jsx>{`
        /* Form Container Responsive Styles */
        .form-container {
          max-width: 1000px;
          margin: 1rem auto;
          padding: 1.25rem;
          width: 100%;
        }

        @media (min-width: 768px) {
          .form-container {
            margin: 3rem auto;
            padding: 2.5rem;
          }
        }

        /* Form Title Responsive Styles */
        .form-title {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }

        @media (min-width: 768px) {
          .form-title {
            font-size: 2rem;
          }
        }

        /* Popup Responsive Styles */
        .popup-container {
          position: fixed;
          bottom: 20px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          z-index: 1000;
          padding: 0 1rem;
        }

        .popup-message {
          background-color: white;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: slideUp 0.3s ease-out;
          width: 100%;
          max-width: 500px;
        }

        @media (min-width: 768px) {
          .popup-message {
            padding: 16px 24px;
          }
        }

        .popup-content {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
        }

        @media (min-width: 768px) {
          .popup-content {
            gap: 12px;
            font-size: 1rem;
          }
        }

        .popup-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        @media (min-width: 768px) {
          .popup-icon {
            width: 24px;
            height: 24px;
          }
        }

        /* Form Input Responsive Styles */
        input,
        textarea {
          font-size: 0.875rem;
          padding: 0.625rem;
        }

        @media (min-width: 768px) {
          input,
          textarea {
            font-size: 1rem;
            padding: 0.75rem 1rem;
          }
        }

        /* Submit Button Responsive Styles */
        .submit-btn {
          padding: 0.75rem 1.5rem;
          font-size: 0.875rem;
          max-width: 180px;
        }

        @media (min-width: 768px) {
          .submit-btn {
            padding: 0.875rem 2rem;
            font-size: 1rem;
            max-width: 200px;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Touch-friendly styles for mobile */
        @media (max-width: 767px) {
          input,
          textarea,
          button {
            min-height: 44px; /* Minimum touch target size */
          }

          .form-group {
            margin-bottom: 1rem;
          }

          textarea {
            min-height: 100px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactForm;
